import { Eye, RefreshCw, Save, Send } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { Navigate, useNavigate } from 'react-router-dom'
import { ApiError, apiErrorMessage, getAdminToken, setAdminToken } from '../../api/client'
import { AdminHeader } from './adminAccess'
import { adminApi } from '../../api/endpoints'
import { ErrorMessage } from '../../components/ErrorMessage'
import type { EmailTemplateResponse } from '../../types/api'

export function AdminEmailTemplatesPage() {
  const navigate = useNavigate()
  const [templates, setTemplates] = useState<EmailTemplateResponse[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)

  // The draft is held apart from the loaded row so that Preview can render what is in the
  // textarea rather than what was last saved - the useful moment to look is before saving.
  const [subject, setSubject] = useState('')
  const [bodyHtml, setBodyHtml] = useState('')
  const [enabled, setEnabled] = useState(true)

  const [previewHtml, setPreviewHtml] = useState<string | null>(null)
  const [testTo, setTestTo] = useState('')
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const signOut = useCallback(() => {
    setAdminToken(null)
    navigate('/admin/login', { replace: true })
  }, [navigate])

  const selected = templates.find((t) => t.id === selectedId) ?? null
  const dirty =
    selected !== null &&
    (subject !== selected.subject || bodyHtml !== selected.bodyHtml || enabled !== selected.enabled)

  const select = useCallback((t: EmailTemplateResponse) => {
    setSelectedId(t.id)
    setSubject(t.subject)
    setBodyHtml(t.bodyHtml)
    setEnabled(t.enabled)
    setPreviewHtml(null)
    setNotice(null)
    setError(null)
  }, [])

  const load = useCallback(() => {
    setLoading(true)
    adminApi
      .emailTemplates()
      .then((rows) => {
        setTemplates(rows)
        setSelectedId((current) => {
          if (current && rows.some((r) => r.id === current)) return current
          const first = rows[0]
          if (first) {
            setSubject(first.subject)
            setBodyHtml(first.bodyHtml)
            setEnabled(first.enabled)
            return first.id
          }
          return null
        })
      })
      .catch((err) => {
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
          signOut()
          return
        }
        setError(apiErrorMessage(err, 'Could not load the email templates'))
      })
      .finally(() => setLoading(false))
  }, [signOut])

  useEffect(() => {
    if (getAdminToken()) load()
  }, [load])

  if (!getAdminToken()) return <Navigate to="/admin/login" replace />

  async function save() {
    if (!selected) return
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      const updated = await adminApi.updateEmailTemplate(selected.id, { subject, bodyHtml, enabled })
      setTemplates((rows) => rows.map((r) => (r.id === updated.id ? updated : r)))
      setNotice('Saved. The next email of this kind uses it.')
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save this template'))
    } finally {
      setBusy(false)
    }
  }

  async function preview(sendTo?: string) {
    if (!selected) return
    setBusy(true)
    setError(null)
    setNotice(null)
    try {
      const res = await adminApi.previewEmailTemplate(selected.id, { subject, bodyHtml, sendTo })
      setPreviewHtml(res.bodyHtml)
      if (res.sent) setNotice(`Test email sent to ${sendTo}.`)
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not render this template'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="admin-console min-h-screen bg-paper lg:pl-64">
      <AdminHeader current="emailTemplates" onSignOut={signOut} width="max-w-6xl" />

      <main className="mx-auto max-w-6xl px-6 py-8">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-extrabold text-ink-900">Email templates</h1>
            <p className="mt-1 text-sm text-ink-500">
              The HTML the app sends. Editing one takes effect on the next email - no deploy.
            </p>
          </div>
          <button
            type="button"
            onClick={load}
            className="flex items-center gap-1 text-sm font-semibold text-ink-500 hover:text-ink-900"
          >
            <RefreshCw size={16} /> Reload
          </button>
        </div>

        <ErrorMessage message={error} />
        {notice && (
          <p className="mb-4 rounded-lg bg-green-50 px-4 py-2 text-sm text-green-800">{notice}</p>
        )}

        {loading ? (
          <p className="text-ink-500">Loading...</p>
        ) : templates.length === 0 ? (
          <p className="text-ink-500">No templates yet.</p>
        ) : (
          <div className="grid gap-6 lg:grid-cols-[240px_1fr]">
            <aside className="space-y-2">
              {templates.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => select(t)}
                  className={`block w-full rounded-lg border px-3 py-2 text-left text-sm ${
                    t.id === selectedId
                      ? 'border-sun-500 bg-sun-50 font-semibold text-ink-900'
                      : 'border-ink-100 bg-white text-ink-500 hover:border-ink-200'
                  }`}
                >
                  <span className="block">{t.name}</span>
                  <span className="mt-0.5 block font-mono text-[11px] text-ink-400">{t.code}</span>
                  {!t.enabled && (
                    <span className="mt-1 inline-block rounded bg-ink-100 px-1.5 py-0.5 text-[11px] text-ink-500">
                      disabled
                    </span>
                  )}
                </button>
              ))}
            </aside>

            {selected && (
              <section className="space-y-4">
                {selected.description && (
                  <p className="text-sm text-ink-500">{selected.description}</p>
                )}

                <label className="block">
                  <span className="label">Subject</span>
                  <input
                    className="input"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                  />
                </label>

                <label className="block">
                  <span className="label">HTML body</span>
                  <textarea
                    className="input min-h-[320px] font-mono text-xs"
                    spellCheck={false}
                    value={bodyHtml}
                    onChange={(e) => setBodyHtml(e.target.value)}
                  />
                </label>

                <div className="rounded-lg bg-white p-3 text-xs text-ink-500">
                  <p className="font-semibold text-ink-900">Placeholders</p>
                  <p className="mt-1">
                    Available:{' '}
                    {selected.availableVariables.map((v) => (
                      <code key={v} className="mr-1 rounded bg-ink-50 px-1 py-0.5 font-mono">
                        {v}
                      </code>
                    ))}
                  </p>
                  {selected.requiredVariables.length > 0 && (
                    <p className="mt-1 text-ink-900">
                      Required (the save is rejected without it):{' '}
                      {selected.requiredVariables.map((v) => (
                        <code key={v} className="mr-1 rounded bg-sun-50 px-1 py-0.5 font-mono">
                          {v}
                        </code>
                      ))}
                    </p>
                  )}
                </div>

                <label className="flex items-center gap-2 text-sm text-ink-900">
                  <input
                    type="checkbox"
                    checked={enabled}
                    onChange={(e) => setEnabled(e.target.checked)}
                  />
                  Enabled
                  <span className="text-ink-400">
                    - when off, the app falls back to its plain-text built-in body
                  </span>
                </label>

                <div className="flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={save}
                    disabled={busy || !dirty}
                    className="btn-sun flex items-center gap-1 disabled:opacity-50"
                  >
                    <Save size={16} /> {busy ? 'Working...' : 'Save'}
                  </button>
                  <button
                    type="button"
                    onClick={() => preview()}
                    disabled={busy}
                    className="flex items-center gap-1 rounded-full border border-ink-200 px-4 py-2 text-sm font-semibold text-ink-900 hover:border-ink-300"
                  >
                    <Eye size={16} /> Preview
                  </button>
                  <span className="flex items-center gap-2">
                    <input
                      className="input !mt-0 w-56"
                      type="email"
                      placeholder="you@example.com"
                      value={testTo}
                      onChange={(e) => setTestTo(e.target.value)}
                    />
                    <button
                      type="button"
                      onClick={() => preview(testTo.trim())}
                      disabled={busy || !testTo.trim()}
                      className="flex items-center gap-1 rounded-full border border-ink-200 px-4 py-2 text-sm font-semibold text-ink-900 hover:border-ink-300 disabled:opacity-50"
                    >
                      <Send size={16} /> Send test
                    </button>
                  </span>
                </div>

                {selected.updatedBy && (
                  <p className="text-xs text-ink-400">Last edited by {selected.updatedBy}.</p>
                )}

                {previewHtml !== null && (
                  <div>
                    <p className="label">Preview (sample values)</p>
                    {/* Sandboxed with no allow-scripts: this is operator-authored HTML rendered
                        inside the ops app, and a stored <script> would otherwise run against an
                        admin session. Email clients do not run scripts either, so the sandbox
                        also shows a truer picture of what gets delivered. */}
                    <iframe
                      title="Email preview"
                      sandbox=""
                      srcDoc={previewHtml}
                      className="h-[520px] w-full rounded-lg border border-ink-100 bg-white"
                    />
                  </div>
                )}
              </section>
            )}
          </div>
        )}
      </main>
    </div>
  )
}
